var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { customElement, property, state } from 'lit/decorators.js';
import { html } from 'lit';
import { createRef, ref } from 'lit/directives/ref.js';
import { BwElement } from './base/bw-element.js';
// CSS lives in packages/ui-components/src/styles/bw-track-cell.css —
// consumers must import it (Storybook preview, web SPA, desktop renderer).
// Light-DOM convention per I-11 — `static styles` is a no-op without shadow root.
// IMPORTANT: consumer must ALSO import bw-volume-bar.css + bw-vu-meter.css +
// bw-button.css + bw-knob.css + bw-dropdown.css (all embedded atomics).
// See packages/ui-components/README.md "Light DOM + CSS prefix" for full convention.
import './bw-dropdown.js';
const DEFAULT_TRACK = { id: '', name: '—' };
function fileBasename(path) {
    if (!path)
        return '—';
    const parts = path.split(/[\\/]/);
    return parts[parts.length - 1] || '—';
}
function stageOutLabel(channel) {
    if (channel == null || channel === -1)
        return 'out main';
    return `out ${channel + 1}`;
}
let BwTrackCell = class BwTrackCell extends BwElement {
    constructor() {
        super(...arguments);
        this.mode = 'rehearsal';
        this.track = DEFAULT_TRACK;
        this.vu = 0;
        this.armUiVisible = false;
        this.panDisabled = false;
        this.hasOtherSoloed = false;
        this.soloed = false;
        this.inputChannels = [];
        this.outputChannels = [];
        this.panFormatter = (v) => {
            if (Math.abs(v) < 0.02)
                return 'Center';
            return v < 0 ? `L ${Math.round(-v * 100)}%` : `R ${Math.round(v * 100)}%`;
        };
        this._editing = false;
        this._nameInputRef = createRef();
        this._onMuteClick = (e) => {
            e.stopPropagation();
            this.dispatchEvent(new CustomEvent('bw-mute-toggle', {
                detail: { id: this.track.id, muted: !this.track.muted },
                bubbles: true, composed: true,
            }));
        };
        this._onSoloClick = (e) => {
            e.stopPropagation();
            this.dispatchEvent(new CustomEvent('bw-solo-toggle', {
                detail: { id: this.track.id, soloed: !this.track.soloed },
                bubbles: true, composed: true,
            }));
        };
        this._onArmClick = (e) => {
            e.stopPropagation();
            this.dispatchEvent(new CustomEvent('bw-arm-toggle', {
                detail: { id: this.track.id, armed: !this.track.armed },
                bubbles: true, composed: true,
            }));
        };
        this._onVolumeInput = (e) => {
            e.stopPropagation();
            const detail = e.detail;
            this.dispatchEvent(new CustomEvent('bw-volume-input', {
                detail: { id: this.track.id, value: detail.value },
                bubbles: true, composed: true,
            }));
        };
        this._onVolumeChange = (e) => {
            e.stopPropagation();
            const detail = e.detail;
            this.dispatchEvent(new CustomEvent('bw-volume-change', {
                detail: { id: this.track.id, value: detail.value },
                bubbles: true, composed: true,
            }));
        };
        this._onPanInput = (e) => {
            e.stopPropagation();
            const detail = e.detail;
            this.dispatchEvent(new CustomEvent('bw-pan-input', {
                detail: { id: this.track.id, value: detail.value },
                bubbles: true, composed: true,
            }));
        };
        this._onPanChange = (e) => {
            e.stopPropagation();
            const detail = e.detail;
            this.dispatchEvent(new CustomEvent('bw-pan-change', {
                detail: { id: this.track.id, value: detail.value },
                bubbles: true, composed: true,
            }));
        };
        this._onInputDropdownChange = (e) => {
            e.stopPropagation();
            const value = Number(e.detail.value);
            this.dispatchEvent(new CustomEvent('bw-input-change', {
                detail: { id: this.track.id, inputChannel: value },
                bubbles: true, composed: true,
            }));
        };
        this._onOutputDropdownChange = (e) => {
            e.stopPropagation();
            const value = Number(e.detail.value);
            this.dispatchEvent(new CustomEvent('bw-output-change', {
                detail: { id: this.track.id, outputChannel: value },
                bubbles: true, composed: true,
            }));
        };
        this._onNameDblclick = () => {
            if (this.mode !== 'rehearsal')
                return;
            this._editing = true;
        };
        this._onNameKeydown = (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                this._commitRename(e.target);
            }
            else if (e.key === 'Escape') {
                e.preventDefault();
                this._editing = false;
            }
        };
        this._onNameBlur = (e) => {
            if (!this._editing)
                return;
            this._commitRename(e.target);
        };
        this._onStripeClick = (e) => {
            if (this.mode !== 'rehearsal')
                return;
            if (!e.metaKey && !e.ctrlKey)
                return;
            const target = e.currentTarget;
            const rect = target.getBoundingClientRect();
            this.dispatchEvent(new CustomEvent('bw-color-pick-request', {
                detail: { id: this.track.id, anchor: { x: rect.right, y: rect.top } },
                bubbles: true, composed: true,
            }));
        };
        this._onContextMenu = (e) => {
            if (this.mode !== 'rehearsal')
                return;
            e.preventDefault();
            this.dispatchEvent(new CustomEvent('bw-context-menu-request', {
                detail: { id: this.track.id, x: e.clientX, y: e.clientY },
                bubbles: true, composed: true,
            }));
        };
        this._onDragStart = (e) => {
            if (this.mode !== 'rehearsal')
                return;
            if (e.dataTransfer) {
                e.dataTransfer.setData('text/plain', this.track.id);
                e.dataTransfer.effectAllowed = 'move';
            }
            this.classList.add('is-dragging');
        };
        this._onDragEnd = () => {
            this.classList.remove('is-dragging');
        };
        this._onDragOver = (e) => {
            if (this.mode !== 'rehearsal')
                return;
            e.preventDefault();
            if (e.dataTransfer)
                e.dataTransfer.dropEffect = 'move';
        };
        this._onDrop = (e) => {
            if (this.mode !== 'rehearsal')
                return;
            e.preventDefault();
            const fromId = e.dataTransfer?.getData('text/plain');
            if (!fromId || fromId === this.track.id)
                return;
            this.dispatchEvent(new CustomEvent('bw-reorder-request', {
                detail: { fromId, toId: this.track.id },
                bubbles: true, composed: true,
            }));
        };
    }
    render() {
        const isRehearsal = this.mode === 'rehearsal';
        const isStage = this.mode === 'stage';
        const track = this.track;
        return html `
      <div
        class="tr-stripe"
        role=${isRehearsal ? 'button' : 'presentation'}
        aria-label=${isRehearsal ? 'Track stripe — Cmd-click to change color, drag to reorder' : 'Track stripe'}
        .draggable=${this.mode === 'rehearsal'}
        @click=${this._onStripeClick}
        @dragstart=${this._onDragStart}
      ></div>
      <div class="tr-center-stack">
        <div class="tr-name-row">
          ${this._editing
            ? html `<input
                class="tr-name-inp"
                aria-label="Track name"
                @keydown=${this._onNameKeydown}
                @blur=${this._onNameBlur}
                ${ref(this._nameInputRef)}
              />`
            : html `<div class="tr-name" @dblclick=${this._onNameDblclick}>${track.name || '—'}</div>`}
          ${isRehearsal ? html `<div class="tr-file">${fileBasename(track.filePath)}</div>` : ''}
          <bw-button
            data-role="ms"
            data-action="mute"
            aria-label="Mute"
            kind="chip"
            size=${this.mode === 'stage' ? 'lg' : 'xs'}
            tone=${track.muted ? 'red' : 'default'}
            ?pressed=${!!track.muted}
            @click=${this._onMuteClick}
          >M</bw-button>
          <bw-button
            data-role="ms"
            data-action="solo"
            aria-label="Solo"
            kind="chip"
            size=${this.mode === 'stage' ? 'lg' : 'xs'}
            tone="default"
            ?pressed=${!!track.soloed}
            @click=${this._onSoloClick}
          >S</bw-button>
        </div>
        ${isStage ? html `
          <div class="tr-chips-row">
            <span class="bw-tc-out-inline">${stageOutLabel(track.outputChannel)}</span>
          </div>
        ` : ''}
        ${isRehearsal ? html `
          <div class="tr-chips-row">
            ${this.armUiVisible ? html `
              <bw-button
                data-role="ms"
                data-action="arm"
                aria-label="Record arm"
                kind="chip"
                size=${this.mode === 'stage' ? 'lg' : 'xs'}
                tone=${track.armed ? 'red' : 'default'}
                ?pressed=${!!track.armed}
                @click=${this._onArmClick}
              >R</bw-button>
            ` : ''}
            <bw-dropdown
              data-role="in"
              class="tr-in-select"
              size="sm"
              .options=${this._toDropdownOpts(this.inputChannels)}
              .value=${String(track.inputChannel ?? '')}
              @bw-change=${this._onInputDropdownChange}
            ></bw-dropdown>
            <bw-dropdown
              data-role="out"
              class="tr-out-select"
              size="sm"
              .options=${this._toDropdownOpts(this.outputChannels)}
              .value=${String(track.outputChannel ?? '')}
              @bw-change=${this._onOutputDropdownChange}
            ></bw-dropdown>
          </div>
        ` : ''}
        <div class="tr-volume-row">
          <bw-volume-bar
            .value=${track.volume ?? 1}
            .vu=${this.vu}
            ?idle=${this._isVolumeIdle()}
            @bw-input=${this._onVolumeInput}
            @bw-change=${this._onVolumeChange}
          ></bw-volume-bar>
        </div>
      </div>
      ${isRehearsal ? html `
        <div class="tr-knob-cell">
          <!-- Pan mirrors volume's input/change split: bw-pan-input = live
               (engine) during drag, bw-pan-change = commit (persist). Wiring
               only the commit to persistence avoids a per-frame re-render storm
               (the pan-jitter bug). -->
          <bw-knob
            .value=${track.pan ?? 0}
            min="-1"
            max="1"
            ?disabled=${this.panDisabled}
            @bw-knob-input=${this._onPanInput}
            @bw-knob-change=${this._onPanChange}
          ></bw-knob>
        </div>
      ` : ''}
    `;
    }
    updated(changed) {
        if (changed.has('track')) {
            if (this.track.color) {
                this.style.setProperty('--c', this.track.color);
            }
            else {
                this.style.removeProperty('--c');
            }
            // Reflect soloed from track for CSS sibling-aware dim.
            // Set attribute directly (not via property) so it lands within the
            // same update cycle — assigning the reflected property here would
            // schedule a second update, not visible to the test after the first
            // updateComplete await.
            if (this.track.soloed) {
                this.setAttribute('soloed', '');
                this.soloed = true;
            }
            else {
                this.removeAttribute('soloed');
                this.soloed = false;
            }
        }
        if (changed.has('_editing') && this._editing) {
            // Seed input value imperatively, ONLY at edit-mode entry.
            // Don't bind via template `.value=${track.name}` — that re-applies on
            // every Lit re-render and would clobber what the user is typing if
            // the consumer mutates `track` mid-edit (consumer-race guard).
            const input = this._nameInputRef.value;
            if (input) {
                input.value = this.track.name || '';
                input.focus();
                input.select();
            }
        }
    }
    _isVolumeIdle() {
        if (this.mode !== 'rehearsal')
            return false;
        if (!this.armUiVisible)
            return true;
        return !this.track.armed;
    }
    _toDropdownOpts(opts) {
        return opts.map((o) => ({ value: String(o.value), label: o.label }));
    }
    _commitRename(input) {
        const next = input.value.trim();
        if (next && next !== this.track.name) {
            this.dispatchEvent(new CustomEvent('bw-rename', {
                detail: { id: this.track.id, name: next },
                bubbles: true, composed: true,
            }));
        }
        this._editing = false;
    }
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('contextmenu', this._onContextMenu);
        this.addEventListener('dragover', this._onDragOver);
        this.addEventListener('drop', this._onDrop);
        this.addEventListener('dragend', this._onDragEnd);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.removeEventListener('contextmenu', this._onContextMenu);
        this.removeEventListener('dragover', this._onDragOver);
        this.removeEventListener('drop', this._onDrop);
        this.removeEventListener('dragend', this._onDragEnd);
    }
};
__decorate([
    property({ type: String, reflect: true })
], BwTrackCell.prototype, "mode", void 0);
__decorate([
    property({ attribute: false })
], BwTrackCell.prototype, "track", void 0);
__decorate([
    property({ type: Number })
], BwTrackCell.prototype, "vu", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'arm-ui-visible' })
], BwTrackCell.prototype, "armUiVisible", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'pan-disabled' })
], BwTrackCell.prototype, "panDisabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true, attribute: 'has-other-soloed' })
], BwTrackCell.prototype, "hasOtherSoloed", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], BwTrackCell.prototype, "soloed", void 0);
__decorate([
    property({ attribute: false })
], BwTrackCell.prototype, "inputChannels", void 0);
__decorate([
    property({ attribute: false })
], BwTrackCell.prototype, "outputChannels", void 0);
__decorate([
    property({ attribute: false })
], BwTrackCell.prototype, "panFormatter", void 0);
__decorate([
    state()
], BwTrackCell.prototype, "_editing", void 0);
BwTrackCell = __decorate([
    customElement('bw-track-cell')
], BwTrackCell);
export { BwTrackCell };
//# sourceMappingURL=bw-track-cell.js.map